import { getCategoryColor, getPricingGroupCode } from "./aiMappingColors";
import {
  getAreaBounds,
  LatLngPair,
  parseAreaCoordinates,
  pixelBBoxCenterToGeo,
  pixelPointToGeo,
} from "./aiMappingGeo";
import {
  AiMappingAreaLike,
  AiMappingDetectionGroup,
  AiMappingDetectionLike,
  AiMappingGeoContext,
  AiMappingGeoDetection,
  AiMappingGeoSegmentation,
  AiMappingSegmentationLike,
} from "./aiMappingTypes";

export function filterActiveDetections<T extends AiMappingDetectionLike>(detections: T[]): T[] {
  return detections.filter((detection) => !detection.isDeleted);
}

export function getAreaGeoContext(area: AiMappingAreaLike | null | undefined): AiMappingGeoContext {
  if (!area) return { bounds: null, imageWidth: null, imageHeight: null };
  return {
    bounds: getAreaBounds(parseAreaCoordinates(area.coordinates)),
    imageWidth: area.imageWidth ?? null,
    imageHeight: area.imageHeight ?? null,
  };
}

export function mapDetectionsToGeo<T extends AiMappingDetectionLike>(
  detections: T[],
  context: AiMappingGeoContext
): AiMappingGeoDetection<T>[] {
  return filterActiveDetections(detections).map((detection) => ({
    detection,
    color: getCategoryColor(detection.category, detection.color),
    position: pixelBBoxCenterToGeo(
      detection.bbox,
      context.imageWidth,
      context.imageHeight,
      context.bounds
    ),
  }));
}

export function groupDetectionsByCategory<T extends AiMappingDetectionLike>(
  detections: T[]
): AiMappingDetectionGroup<T>[] {
  const groups = new Map<string, AiMappingDetectionGroup<T>>();
  for (const detection of filterActiveDetections(detections)) {
    const groupKey = getPricingGroupCode(detection.category);
    const existing = groups.get(groupKey);
    if (existing) {
      existing.items.push(detection);
      existing.count += 1;
    } else {
      groups.set(groupKey, {
        category: groupKey,
        color: getCategoryColor(detection.category, detection.color),
        count: 1,
        items: [detection],
      });
    }
  }
  const sorted = Array.from(groups.values());
  for (const group of sorted) {
    group.items.sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0));
  }
  sorted.sort((a, b) => b.count - a.count || a.category.localeCompare(b.category));
  return sorted;
}

export function countActiveDetections(detections: AiMappingDetectionLike[]): number {
  return filterActiveDetections(detections).length;
}

export function pixelPolygonToGeoRing(
  polygon: number[],
  context: AiMappingGeoContext
): LatLngPair[] | null {
  if (!context.bounds || !context.imageWidth || !context.imageHeight) return null;
  if (!Array.isArray(polygon) || polygon.length < 6 || polygon.length % 2 !== 0) return null;
  const ring: LatLngPair[] = [];
  for (let i = 0; i < polygon.length; i += 2) {
    const point = pixelPointToGeo(
      polygon[i],
      polygon[i + 1],
      context.imageWidth,
      context.imageHeight,
      context.bounds
    );
    if (!point) return null;
    ring.push(point);
  }
  return ring;
}

export function mapSegmentationsToGeo<T extends AiMappingSegmentationLike>(
  segmentations: T[],
  context: AiMappingGeoContext
): AiMappingGeoSegmentation[] {
  return segmentations
    .filter((segmentation) => !segmentation.isDeleted)
    .map((segmentation) => ({
      segmentation,
      color: getCategoryColor(segmentation.category, segmentation.color),
      ring: pixelPolygonToGeoRing(segmentation.polygon, context),
    }));
}

export function totalSegmentedAreaSqft(segmentations: AiMappingSegmentationLike[]): number {
  return segmentations
    .filter((segmentation) => !segmentation.isDeleted)
    .reduce((sum, segmentation) => sum + (segmentation.areaSqft ?? 0), 0);
}

export const sqftToSquareYards = (sqft: number): number => sqft / 9;
